import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Coffin } from '../../types';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from './index';
import PriceDisplay from './PriceDisplay';
import StockIndicator from './StockIndicator';
import QuickBadge from './QuickBadge';
import toast from 'react-hot-toast';

interface CoffinSaleCardProps {
  coffin: Coffin;
  onSell: () => void;
  onEdit: () => void;
  onDelete: () => void;
  badge?: 'bestseller' | 'limited' | 'premium' | 'new' | null;
}

const CoffinSaleCard: React.FC<CoffinSaleCardProps> = ({
  coffin,
  onSell,
  onEdit,
  onDelete,
  badge
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isUnavailable = !coffin.isAvailable || coffin.stock === 0;
  const isLowStock = coffin.stock === 1 && coffin.isAvailable;

  return (
      <div
        className={clsx(
          'group relative rounded-2xl bg-white border p-4 transition-all duration-200',
          isUnavailable ? 'border-gray-200 opacity-75' : 'border-gray-100 hover:border-gray-300 hover:shadow-md'
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Encabezado: identidad + estado */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0">
            {badge && <QuickBadge variant={badge} className="mb-1" />}
            {!badge && isLowStock && <QuickBadge variant="limited" className="mb-1" />}

            <h3 className="text-base font-semibold text-gray-900 truncate">
              {coffin.name}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              ID: {coffin.id}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StockIndicator current={coffin.stock} showBadge={isLowStock} size="sm" />

            {isUnavailable ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700">
                No disponible para venta
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-800">
                Disponible
              </span>
            )}
          </div>
        </div>

        {/* Bloque principal: precio y atributos */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between gap-4 mb-3">
            <div>
              <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                Precio
              </span>
              <PriceDisplay amount={coffin.price} emphasize={true} className="mt-1 text-xl" />
            </div>
            <div className="text-right text-sm text-gray-600">
              <span className="block text-xs text-gray-500">Stock actual</span>
              <span className="font-medium text-gray-900">
                {coffin.stock} unidad{coffin.stock === 1 ? '' : 'es'}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center text-xs font-medium text-gray-700 bg-gray-50 px-2.5 py-1 rounded-full">
              Material: <span className="ml-1 capitalize">{coffin.material}</span>
            </span>
            <span className="inline-flex items-center text-xs font-medium text-gray-700 bg-gray-50 px-2.5 py-1 rounded-full">
              Tamaño: <span className="ml-1 capitalize">{coffin.size}</span>
            </span>
          </div>
        </div>

        {/* Descripción breve */}
        {coffin.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-3">
            {coffin.description}
          </p>
        )}

        {/* Acciones */}
        <div className="space-y-2 pt-2 border-t border-gray-100">
          {/* CTA principal */}
          {!isUnavailable ? (
            <button
              onClick={onSell}
              className={clsx(
                'w-full inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white',
                'bg-gray-900 hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-900',
                'transition-colors'
              )}
            >
              Vender ataúd
            </button>
          ) : (
            <button
              disabled
              className="w-full inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-500 bg-gray-100 cursor-not-allowed"
            >
              No disponible para venta
            </button>
          )}

          {/* Acciones secundarias */}
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={onEdit}
              className="inline-flex items-center justify-center flex-1 rounded-lg px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <PencilIcon className="h-4 w-4 mr-1.5 text-gray-500" />
              Editar
            </button>
            <button
              onClick={onDelete}
              className="inline-flex items-center justify-center flex-1 rounded-lg px-3 py-2 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <TrashIcon className="h-4 w-4 mr-1.5 text-red-500" />
              Eliminar
            </button>
          </div>
        </div>
      </div>
  );
};

export default CoffinSaleCard;
